// 📁 src/components/invoice/InvoicePreviewPage.jsx

import React, { useEffect, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function InvoicePreviewPage() {
  const [invoice, setInvoice] = useState(null);
  const invoiceRef = useRef(null);

  useEffect(() => {
    const scoped = JSON.parse(sessionStorage.getItem('invoicePreviewData') || '{}');
    const { email, invoiceNo } = scoped || {};

    if (email && invoiceNo) {
      const archive = JSON.parse(localStorage.getItem(`invoiceArchive_${email}`) || '[]');
      const match = archive.find((entry) => entry.invoiceNo === invoiceNo);

      if (match) {
        setInvoice({
          invoiceNo: match.invoiceNo || 'N/A',
          billingDate: match.billingDate || new Date().toISOString(),
          tenantId: match.tenantId,
          companyName: match.companyName,
          email: match.email,
          moduleName: match.moduleName,
          billingCycle: match.billingCycle,
          modulePeriod: match.modulePeriod,
          currency: match.currency,
          baseAmount: Number(match.baseAmount || 0),
          addOnAmount: Number(match.addOnAmount || 0),
          annualDiscountRate: Number(match.annualDiscountRate || 0),
          totalAmount: Number(match.totalAmount || 0),
          method: match.method,
          notes: match.notes || {
            annualApplied: '',
            footer: 'Thank you for your subscription.'
          }
        });
      }
    }
  }, []);

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    await new Promise((resolve) => setTimeout(resolve, 300));
    const canvas = await html2canvas(invoiceRef.current, {
      scale: 2,
      useCORS: true
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
    pdf.save(`invoice_${invoice.moduleName}.pdf`);
  };

  if (!invoice) return <div className="p-6 text-red-600">No invoice found.</div>;

  const {
    invoiceNo,
    billingDate,
    tenantId,
    companyName,
    email: customerEmail,
    moduleName,
    billingCycle,
    modulePeriod,
    currency,
    baseAmount,
    addOnAmount,
    annualDiscountRate,
    totalAmount,
    method,
    notes
  } = invoice;

  const format = (value) => `${currency} ${value.toFixed(2)}`;
  const isAnnual = billingCycle === 'annual';

  const originalAmount =
    isAnnual && annualDiscountRate > 0 ? baseAmount / (1 - annualDiscountRate / 100) : baseAmount;

  const annualDiscountAmount = isAnnual && annualDiscountRate > 0 ? originalAmount - baseAmount : 0;

  return (
    <div
      ref={invoiceRef}
      className="max-w-3xl mx-auto p-8 bg-white shadow-lg border border-gray-300 mt-6"
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-indigo-700">Talentraxion Invoice</h1>
          <p className="text-sm text-gray-500">Invoice No: {invoiceNo}</p>
          <p className="text-sm text-gray-500">
            Billing Date: {new Date(billingDate).toLocaleString('en-BD')}
          </p>
        </div>
        <div className="text-right text-sm text-gray-600">
          <p>
            Company: <strong>{companyName}</strong>
          </p>
          <p>Tenant ID: {tenantId}</p>
          <p>Email: {customerEmail}</p>
        </div>
      </div>

      <hr className="mb-4" />

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Subscription Details</h2>
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
          <p>
            Module: <strong>{moduleName}</strong>
          </p>
          <p>
            Billing Cycle: <strong>{billingCycle}</strong>
          </p>
          <p>
            Module Period: <strong>{modulePeriod}</strong>
          </p>
          <p>
            Payment Method: <strong>{method}</strong>
          </p>
        </div>
      </div>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Amount Breakdown</h2>
        <div className="text-sm text-gray-700 space-y-1">
          {isAnnual ? (
            <>
              <p>Base Amount (before discount): {format(originalAmount)}</p>
              <p>
                Annual Discount ({annualDiscountRate}%): −{format(annualDiscountAmount)}
              </p>
              <p>Promo Discount (0%): −{format(0)}</p>
            </>
          ) : (
            <>
              <p>Base Amount: {format(baseAmount)}</p>
              <p>Promo Discount (0%): −{format(0)}</p>
            </>
          )}
          <p className="font-semibold text-indigo-700 mt-2">
            Total Amount (excluding VAT & AIT): {format(baseAmount)}
          </p>
        </div>
      </div>

      <div className="mb-6 text-sm text-gray-600">
        <p>{notes?.annualApplied}</p>
        <p className="mt-2 italic">{notes?.footer}</p>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleDownloadPDF}
          className="px-4 py-2 bg-green-600 text-white rounded shadow hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-indigo-600 text-white rounded shadow hover:bg-indigo-700"
        >
          Print Invoice
        </button>
        <button
          onClick={() => window.close()}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded shadow hover:bg-gray-400"
        >
          Close
        </button>
      </div>
    </div>
  );
}
